import Banner from '@carvana/ds-banner';
import { datadogRum } from '@datadog/browser-rum';
import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import '|/helpers/loadRUM';
import { createRoot } from 'react-dom/client';
import {
  Navigate,
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
  useLocation,
  useNavigate,
  useRouteError
} from 'react-router-dom';
import '|/index.css';
import '|/scripts/gtm-config';
import '|/scripts/xhr-extensions';
import '|/scripts/number-extensions';
import '@carvana/showroom-css-theme/Theme.css'; // We can get rid of this when we rewrite the components
import '@carvana/showroom-forms/index.css'; // We can get rid of this when we rewrite the components
import '@carvana/showroom-autocomplete/index.css'; // We can get rid of this when we rewrite the components
import '@carvana/header/styles.css';
import '|/ducks/core/reducer';
import '|/ducks/dashboard/reducer';
import '|/ducks/vehicle/reducer';
import styled from 'styled-components';
import { InsuranceOptionsRoute } from '|/app/purchase/flow/external-routes/insurance-options';
import { OrderPlacedRoute } from '|/app/purchase/flow/external-routes/order-placed';
import ErrorBoundary from '|/components/base/error-boundary';
import Loading from '|/components/base/loading';
import { isCarvanaNativeApp } from '|/helpers/is-native-app';
import useDashboardReduxBackfill from '|/hooks/use-dashboard-redux-backfill';
import { useDashboardSummaryRefresh } from '|/hooks/use-dashboard-summary-refresh';
import useGlobalMfeConfig from '|/hooks/use-global-mfe-config';
import useOutageBannerConfig from '|/hooks/use-outage-banner-config';
import useRumViewTracking from '|/hooks/use-rum-view-tracking';
import useSessionTracking from '|/hooks/use-session-tracking';
import { clearScopedSlotPreference } from '|/hooks/use-slot-preference';
import useTrackClickEvents from '|/hooks/use-track-click-events';
import AccountLogin from './app/account-login';
import ChatUi from './app/chat-ui';
import DeveloperControlPanel from './app/developer-control-panel';
import SlateFooter from './app/footer/slate-footer';
import Header, { HeaderMfe } from './app/header';
import VehicleHeader from './app/header/purchase-header/treatment/vehicle-confirmation-header';
import NavigationRail from './app/navigation-rail';
import CheckoutPage from './app/purchase/checkout/checkout-page';
import CheckoutHeader from './app/purchase/checkout/components/checkout-header';
import GuidedFlow from './app/purchase/flow/guided-flow';
import NextStep from './app/purchase/flow/next-step';
import Steps from './app/purchase/flow/steps-router';
import OrderSummaryCio from './app/purchase/shared/order-summary/cio';
import OrderSummary from './app/purchase/shared/order-summary/control';
import stepLayoutStyles from './app/purchase/shared/step-layout.module.css';
import { rollBack, Dashboard, Summary, AvailableOptions, Step } from './app/services';
import VehicleRoutes from './app/vehicle';
import DataLoadRedirect from './components/base/data-load-redirect';
import Error from './components/base/error';
import ProtectedRoute from './components/base/protect-route';
import {
  StepUrlsContextProvider,
  FormSuspense,
  ClickBlockingProvider,
  PurchaseContext,
  CheckItOutProvider,
  SlateProvider,
  useSlate
} from './context';
import SiteProviders from './context/site-providers';
import { AppFrameworkContextProvider } from './helpers/app-framework';
import useScrollRestoration from './hooks/use-scroll-restoration';
import { ReviewSummaryResponse } from './services/purchase-api';
import { DashboardModal } from '../../library/helpers/src';
import { registry } from '../../library/store/src';
import './styles/ds-imports.css';

const Page = styled.div`
  width: 100%;
  background-color: var(--canvas-muted);
  min-height: 100vh;

  &[data-theme='carvana'] {
    color: var(--text-default);
  }
`;

const AppContainer = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  min-height: 100vh;
  box-shadow: 1px 2px 3px 1px rgba(24 53 88 / 10%);
`;

const SlateStepsLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const StepContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  flex: 1 0 auto;

  @media (min-width: 1024px) {
    grid-template-columns: auto 1fr;
  }
`;

const NextStepContainer = styled.div`
  position: relative;
  min-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  grid-column: 2;
`;

const InsuranceStepContainer = styled.div`
  max-width: 627px;
  margin: 0 auto;
  /* Generous bottom padding + safe-area keeps the footer CTAs clear of the Android
     URL-bar dynamic-viewport dead-zone, where flush-to-bottom taps dead-click. */
  padding: var(--spacing-3xl) var(--spacing-2xl)
    calc(env(safe-area-inset-bottom, 0px) + var(--spacing-8xl));
`;

const OutageBannerWrapper = styled.div`
  width: 100%;
`;

const StepRedirect = () => {
  const { pathname } = useLocation();
  return pathname.startsWith('/dashboard') ? (
    <Navigate to="/dashboard" replace={true} />
  ) : (
    <Navigate to={`/steps${pathname}`} replace={true} />
  );
};

const PageWrapper = () => (
  <Page>
    <AppContainer>
      <Root />
    </AppContainer>
  </Page>
);

const ThemeApplier = () => {
  const { isSlateSkin } = useSlate();
  useEffect(() => {
    document.body.dataset.theme = isSlateSkin ? 'slate' : 'carvana';
    return () => {
      delete document.body.dataset.theme;
    };
  }, [isSlateSkin]);
  return null;
};

const StepsLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSlateVehicle } = useSlate();
  if (!isSlateVehicle) return <>{children}</>;
  return <SlateStepsLayout>{children}</SlateStepsLayout>;
};

const getLatestStep = (steps: Step[]) => {
  const orderedSteps = steps.sort((a, b) => a.order - b.order);
  const latestStep = orderedSteps.find(step => step.isEnabled && !step.isComplete);
  return latestStep ?? orderedSteps[0];
};

const Root = () => {
  useGlobalMfeConfig();
  useTrackClickEvents();
  useRumViewTracking();
  useSessionTracking();
  useScrollRestoration();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOrderSummaryModalOpen, setIsOrderSummaryModalOpen] = useState(false);
  const [dashboard, setDashboard] = useState<null | Dashboard>(null);
  const [summary, setSummary] = useState<null | Summary>(null);
  const [availableOptions, setAvailableOptions] = useState<null | AvailableOptions>(null);
  useDashboardReduxBackfill(dashboard, summary, availableOptions);

  const { setVehicleId } = useContext(PurchaseContext);
  const summaryVehicleId = summary?.vehicle?.id ?? null;
  useEffect(() => {
    setVehicleId(summaryVehicleId);
  }, [summaryVehicleId, setVehicleId]);

  // Listen for dashboard/summary refresh events
  useDashboardSummaryRefresh(setDashboard, setSummary);

  const previousLocationKeyRef = useRef(location.key);
  useEffect(() => {
    if (previousLocationKeyRef.current !== location.key && isOrderSummaryModalOpen) {
      setIsOrderSummaryModalOpen(false);
    }
    previousLocationKeyRef.current = location.key;
  }, [location.key, isOrderSummaryModalOpen]);

  const { steps = [] } = dashboard ?? {};
  const latestStep = getLatestStep(steps);
  const { sectionName: latestSectionName = '' } = latestStep ?? {};

  const activeSectionName = latestSectionName;

  const checkItOutBucket =
    summary?.experiments?.find(e => e.key === 'pp-check-it-out')?.bucket ?? 'excluded';
  const isCheckItOutTreatment = checkItOutBucket === 'treatment';

  // Server-sourced experiment — not in ExperimentProvider, requires manual RUM context
  useEffect(() => {
    if (summary === null) return;
    datadogRum.setGlobalContextProperty('experiments.pp-check-it-out', checkItOutBucket);
  }, [summary, checkItOutBucket]);

  const handleEditOrderFromSummary = useCallback(async () => {
    await rollBack({ cancelTransfer: false });
    clearScopedSlotPreference();
    navigate('/next-step');
  }, [navigate]);

  const banner = useOutageBannerConfig();

  return (
    <SlateProvider inventoryType={summary?.vehicle?.inventoryType}>
      <ThemeApplier />
      <CheckItOutProvider isCheckItOutTreatment={isCheckItOutTreatment}>
        <ChatUi />
        <OutageBannerWrapper>
          <Banner
            type="warning"
            open={banner.isVisible}
            align="left"
            icon
            onDismiss={banner.onDismiss}
          >
            <Banner.Headline>{banner.header}</Banner.Headline>
            <Banner.Message>{banner.description}</Banner.Message>
          </Banner>
        </OutageBannerWrapper>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Header
                  orderSummary={summary}
                  dashboard={dashboard}
                  isOrderSummaryModalOpen={isOrderSummaryModalOpen}
                  setIsOrderSummaryModalOpen={setIsOrderSummaryModalOpen}
                />
                <AppFrameworkContextProvider>
                  <FormSuspense>
                    <GuidedFlow />
                  </FormSuspense>
                </AppFrameworkContextProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <Header
                  orderSummary={null}
                  dashboard={dashboard}
                  isOrderSummaryModalOpen={isOrderSummaryModalOpen}
                  setIsOrderSummaryModalOpen={setIsOrderSummaryModalOpen}
                />
                <AppFrameworkContextProvider>
                  <FormSuspense>
                    <GuidedFlow />
                  </FormSuspense>
                </AppFrameworkContextProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="next-step"
            element={
              <ProtectedRoute>
                <Header
                  orderSummary={summary}
                  dashboard={dashboard}
                  isOrderSummaryModalOpen={isOrderSummaryModalOpen}
                  setIsOrderSummaryModalOpen={setIsOrderSummaryModalOpen}
                />
                <StepContainer>
                  <NavigationRail dashboard={dashboard} summary={summary} />
                  <NextStepContainer>
                    <AppFrameworkContextProvider>
                      <FormSuspense>
                        <NextStep
                          setDashboard={setDashboard}
                          setSummary={setSummary}
                          setAvailableOptions={setAvailableOptions}
                        />
                      </FormSuspense>
                    </AppFrameworkContextProvider>
                  </NextStepContainer>
                </StepContainer>
              </ProtectedRoute>
            }
          />
          <Route
            path="vehicle/*"
            element={
              <ProtectedRoute>
                {isCarvanaNativeApp() ? <VehicleHeader /> : <HeaderMfe orderSummary={null} />}
                <AppFrameworkContextProvider>
                  <FormSuspense>
                    <VehicleRoutes />
                  </FormSuspense>
                </AppFrameworkContextProvider>
              </ProtectedRoute>
            }
          />
          {activeSectionName === 'Dashboard' && (
            <Route
              path="steps/order-placed/*"
              element={
                <ProtectedRoute>
                  <DataLoadRedirect dashboard={dashboard} summary={summary}>
                    <OrderPlacedRoute steps={steps} />
                  </DataLoadRedirect>
                </ProtectedRoute>
              }
            ></Route>
          )}
          {isCheckItOutTreatment && (
            <Route
              path="steps/insurance-options/*"
              element={
                <ProtectedRoute>
                  <DataLoadRedirect dashboard={dashboard} summary={summary}>
                    <CheckoutHeader orderSummary={summary} />
                    <AppFrameworkContextProvider>
                      <FormSuspense>
                        <StepUrlsContextProvider steps={steps}>
                          <InsuranceStepContainer>
                            <InsuranceOptionsRoute
                              GetDashboardAndRouteToNextStep={() => {
                                navigate('/next-step');
                              }}
                            />
                          </InsuranceStepContainer>
                        </StepUrlsContextProvider>
                      </FormSuspense>
                    </AppFrameworkContextProvider>
                  </DataLoadRedirect>
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path="checkout"
            element={
              <ProtectedRoute>
                <CheckoutHeader orderSummary={summary} />
                <ClickBlockingProvider>
                  <StepUrlsContextProvider steps={dashboard?.steps ?? []}>
                    <CheckoutPage
                      dashboard={dashboard}
                      setDashboard={setDashboard}
                      summary={summary}
                      setSummary={setSummary}
                      setAvailableOptions={setAvailableOptions}
                      isOrderSummaryModalOpen={isOrderSummaryModalOpen}
                      setIsOrderSummaryModalOpen={setIsOrderSummaryModalOpen}
                    />
                    {isOrderSummaryModalOpen && summary !== null && (
                      <OrderSummaryCio
                        isOpen={isOrderSummaryModalOpen}
                        setIsOpen={setIsOrderSummaryModalOpen}
                        summary={summary as ReviewSummaryResponse}
                        isCheckoutPage={true}
                        onEditOrder={handleEditOrderFromSummary}
                      />
                    )}
                  </StepUrlsContextProvider>
                </ClickBlockingProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="steps/*"
            element={
              <ProtectedRoute>
                <DataLoadRedirect dashboard={dashboard} summary={summary}>
                  <Header
                    orderSummary={summary}
                    dashboard={dashboard}
                    isOrderSummaryModalOpen={isOrderSummaryModalOpen}
                    setIsOrderSummaryModalOpen={setIsOrderSummaryModalOpen}
                  />
                  <AppFrameworkContextProvider>
                    <ClickBlockingProvider>
                      <StepUrlsContextProvider steps={dashboard?.steps ?? []}>
                        <StepsLayout>
                          <StepContainer>
                            <NavigationRail dashboard={dashboard} summary={summary} />
                            <div
                              id="pp-step-background"
                              className={stepLayoutStyles.experimentStepLayoutContainer}
                            >
                              <Steps dashboard={dashboard} />
                              {isOrderSummaryModalOpen &&
                                (isCheckItOutTreatment ? (
                                  <OrderSummaryCio
                                    isOpen={isOrderSummaryModalOpen}
                                    setIsOpen={setIsOrderSummaryModalOpen}
                                    summary={summary as ReviewSummaryResponse}
                                    isCheckoutPage={false}
                                    onEditOrder={handleEditOrderFromSummary}
                                  />
                                ) : (
                                  <OrderSummary
                                    isOpen={isOrderSummaryModalOpen}
                                    setIsOpen={setIsOrderSummaryModalOpen}
                                    summary={summary as ReviewSummaryResponse}
                                  />
                                ))}
                            </div>
                          </StepContainer>
                          <SlateFooter />
                        </StepsLayout>
                      </StepUrlsContextProvider>
                    </ClickBlockingProvider>
                  </AppFrameworkContextProvider>
                </DataLoadRedirect>
              </ProtectedRoute>
            }
          />
          <Route path="account" element={<AccountLogin />} />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <StepRedirect />
              </ProtectedRoute>
            }
          />
        </Routes>
        <DeveloperControlPanel />
        <DashboardModal registry={registry} />
      </CheckItOutProvider>
    </SlateProvider>
  );
};

const container = document.getElementById('root');

const RouteError = () => {
  const error = useRouteError();
  datadogRum.addError(error, {
    customType: 'routeError',
    passedInError: error
  });

  return <Error />;
};

const router = createBrowserRouter(
  [
    {
      path: '/*',
      element: (
        <SiteProviders devicePlatform="web">
          <ErrorBoundary>
            <Suspense fallback={<Loading />}>
              <PageWrapper />
            </Suspense>
          </ErrorBoundary>
        </SiteProviders>
      ),
      errorElement: <RouteError />
    }
  ],
  {
    basename: '/purchase'
  }
);

if (container !== null) {
  const root = createRoot(container);
  root.render(<RouterProvider router={router} />);
}
